import { motion } from "framer-motion";
import { Tent, Home, KeyRound, Award, UtensilsCrossed, Music, Waves, ShowerHead } from "lucide-react";
import { STAY_MODES, STATS, IMAGES } from "../data";

const ICONS = { tent: Tent, barn: Home, cottage: KeyRound };

const FACILITIES = [
  { icon: UtensilsCrossed, label: "Full Meals" },
  { icon: Music, label: "Night DJ & Calypso Band" },
  { icon: Waves, label: "Pool Access · BYOB" },
  { icon: ShowerHead, label: "Washrooms & Amenities" },
];

export const Venue = () => (
  <section id="venue" data-testid="venue-section" className="relative bg-night py-28 md:py-40 overflow-hidden">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-amber mb-6" data-testid="venue-label">
            The Barn House · Kiriberiya, Panadura
          </p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight text-cream leading-[1.05] mb-8">
            Convenient yet <span className="italic text-amber">completely secluded.</span>
          </h2>
          <p className="text-base md:text-lg text-cream/70 leading-relaxed max-w-xl mb-10">
            A short drive from Colombo, yet a world away from it. Sustainable architecture, modern
            hospitality and a living forest combine into a venue that is itself part of the experience —
            recognised as the Best Sustainable Tourism SME Project 2023 and holder of the Good Travel Seal.
          </p>

          <div className="flex items-center gap-4 border border-amber/30 bg-forest p-6 mb-12" data-testid="venue-award-badge">
            <Award className="text-amber shrink-0" size={28} />
            <p className="text-sm text-cream/80 leading-relaxed">
              NSTCS Best Sustainable Tourism (SME) Project 2023 · Level One Good Travel Seal Certification
            </p>
          </div>

          <div className="grid grid-cols-2 gap-px bg-cream/10 border border-cream/10" data-testid="venue-stats-grid">
            {STATS.map((s) => (
              <div key={s.label} className="bg-night p-8">
                <p className="font-display text-4xl md:text-5xl text-amber mb-2">{s.value}</p>
                <p className="text-xs tracking-[0.15em] uppercase text-cream/60">{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6 space-y-10"
        >
          <div className="group relative overflow-hidden clip-frame border border-cream/10">
            <img
              src={IMAGES.pavilionNight}
              alt="The Barn pavilion glowing with string lights at night"
              className="w-full h-[360px] object-cover image-zoom"
              data-testid="venue-image-pavilion"
            />
          </div>

          <div className="space-y-px bg-cream/10 border border-cream/10" data-testid="venue-stay-modes">
            {STAY_MODES.map((m) => {
              const Icon = ICONS[m.icon];
              return (
                <div
                  key={m.name}
                  data-testid={`stay-mode-${m.icon}`}
                  className="group bg-night p-7 flex items-center gap-6 hover:bg-moss transition-colors duration-500"
                >
                  <Icon className="text-amber shrink-0" size={26} />
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-4">
                      <h3 className="font-display text-2xl text-cream group-hover:text-amber transition-colors duration-300">
                        {m.name}
                      </h3>
                      <span className="text-xs tracking-[0.2em] uppercase text-amber whitespace-nowrap">{m.capacity}</span>
                    </div>
                    <p className="text-sm text-cream/60 mt-1">{m.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-3" data-testid="venue-facilities">
            {FACILITIES.map((f) => (
              <span
                key={f.label}
                className="flex items-center gap-2 border border-cream/15 text-cream/70 text-xs tracking-[0.1em] uppercase px-4 py-2"
              >
                <f.icon size={14} className="text-amber" />
                {f.label}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
